import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Gavel, Clock3, ShieldCheck, ArrowUpRight, TrendingUp, Lock } from "lucide-react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

function useCountdown(endsAt) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const i = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(i);
    }, []);
    const target = endsAt ? new Date(endsAt).getTime() : 0;
    const diff = Math.max(0, target - now);
    const d = Math.floor(diff / (1000 * 60 * 60 * 24));
    const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const m = Math.floor((diff / (1000 * 60)) % 60);
    const s = Math.floor((diff / 1000) % 60);
    return { d, h, m, s, ended: diff === 0 };
}

function Cell({ label, value }) {
    return (
        <div className="flex-1 text-center">
            <div className="font-display text-4xl md:text-6xl silver-text tabular-nums">{String(value).padStart(2, "0")}</div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-silver-500 mt-1">{label}</div>
        </div>
    );
}

export default function AuctionPage() {
    const { filmId } = useParams();
    const { user } = useAuth();
    const [film, setFilm] = useState(null);
    const [bids, setBids] = useState([]);
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const pollRef = useRef(null);

    const load = async () => {
        try {
            const [f, b] = await Promise.all([
                api.get(`/films/${filmId}`),
                api.get(`/films/${filmId}/bids`),
            ]);
            setFilm(f.data);
            setBids(b.data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
        }
    };

    useEffect(() => {
        load();
        pollRef.current = setInterval(load, 8000);
        return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filmId]);

    const topBid = bids[0]?.amount_usd || film?.auction_starting_bid_usd || 0;
    const minNext = Math.max(Math.ceil(topBid * 1.05), topBid + 1000);
    const { d, h, m, s, ended } = useCountdown(film?.auction_ends_at);

    const placeBid = async (e) => {
        e.preventDefault();
        const n = Number(amount);
        if (!n || n <= topBid) return toast.error(`Must exceed current top of $${topBid.toLocaleString()}`);
        setBusy(true);
        try {
            await api.post(`/films/${filmId}/bids`, { amount_usd: n });
            setAmount("");
            toast.success("Bid placed");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Bid failed");
        } finally {
            setBusy(false);
        }
    };

    if (!film) {
        return <div className="min-h-screen bg-ink-300"><Navbar /><div className="p-16 text-silver-500">Loading auction…</div></div>;
    }

    const enabled = !!film.auction_enabled;
    const isBuyer = user?.role === "buyer";
    const verified = user?.kyc_status === "verified";

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 pt-12 pb-24">
                <div className="flex items-center gap-3 mb-3 text-[11px] tracking-[0.3em] text-silver-400 uppercase">
                    <Gavel className="w-4 h-4 text-platinum" /> Live Auction
                </div>
                <h1 className="font-display text-5xl md:text-6xl leading-none mb-2">{film.title}</h1>
                <div className="text-silver-400 mb-10">{film.country} · {film.language} · by {film.creator_name}</div>

                {!enabled ? (
                    <div className="border border-white/10 rounded-sm p-10 text-center text-silver-500">
                        No active auction for this film. <Link to={`/film/${filmId}`} className="underline text-platinum">Open deal room instead →</Link>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-[1.2fr_1fr] gap-10">
                        {/* LEFT — countdown + top bid */}
                        <div>
                            <div className="glass rounded-sm p-8 mb-6" data-testid="auction-countdown">
                                <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-5 flex items-center gap-2">
                                    <Clock3 className="w-3.5 h-3.5" /> {ended ? "Auction has ended" : "Time remaining"}
                                </div>
                                <div className="flex gap-4">
                                    <Cell label="Days" value={d} />
                                    <Cell label="Hours" value={h} />
                                    <Cell label="Mins" value={m} />
                                    <Cell label="Secs" value={s} />
                                </div>
                            </div>
                            <div className="glass rounded-sm p-8" data-testid="auction-top-bid">
                                <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-3 flex items-center gap-2">
                                    <TrendingUp className="w-3.5 h-3.5" /> Current top bid
                                </div>
                                <div className="font-display text-5xl md:text-6xl silver-text tabular-nums">
                                    ${Number(topBid).toLocaleString()}
                                </div>
                                {bids[0] && (
                                    <div className="mt-3 text-sm text-silver-400">
                                        by <span className="text-silver-200">{bids[0].bidder_name}</span> · {new Date(bids[0].created_at).toLocaleString()}
                                    </div>
                                )}
                            </div>

                            {/* Place bid form */}
                            <div className="glass rounded-sm p-6 mt-6" data-testid="auction-bid-form">
                                <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-4">Place a bid</div>
                                {!user ? (
                                    <div className="flex items-center gap-3 text-silver-400 text-sm">
                                        <Lock className="w-4 h-4 text-platinum" /> Sign in as a verified buyer to bid.
                                        <Link to={`/login?next=/auction/${filmId}`} className="underline text-platinum">Sign in</Link>
                                    </div>
                                ) : !isBuyer ? (
                                    <div className="flex items-center gap-3 text-silver-400 text-sm">
                                        <Lock className="w-4 h-4 text-silver-500" /> Only buyers can bid. Switch to a buyer account.
                                    </div>
                                ) : !verified ? (
                                    <div className="flex items-center gap-3 text-silver-400 text-sm">
                                        <Lock className="w-4 h-4 text-platinum" /> KYC required before bidding.
                                        <Link to="/kyc" className="underline text-platinum">Verify identity →</Link>
                                    </div>
                                ) : ended ? (
                                    <div className="text-silver-400 text-sm">Bidding has closed.</div>
                                ) : (
                                    <form onSubmit={placeBid} className="flex flex-wrap items-end gap-3">
                                        <div className="flex-1 min-w-[200px]">
                                            <label className="block text-[10px] tracking-widest uppercase text-silver-500 mb-1">Your bid (USD)</label>
                                            <div className="flex items-center border border-white/10 rounded-sm overflow-hidden">
                                                <span className="px-3 text-silver-400 text-sm">$</span>
                                                <input
                                                    type="number"
                                                    min={minNext}
                                                    step="1000"
                                                    value={amount}
                                                    onChange={(e) => setAmount(e.target.value)}
                                                    placeholder={minNext.toLocaleString()}
                                                    className="flex-1 bg-transparent py-3 text-white placeholder:text-silver-500 focus:outline-none"
                                                    data-testid="auction-bid-input"
                                                />
                                            </div>
                                            <div className="text-[10px] text-silver-500 mt-1">
                                                Minimum next bid: ${minNext.toLocaleString()} (+5%)
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={busy}
                                            className="bg-platinum hover:bg-white text-ink-400 px-6 py-3 text-xs tracking-widest uppercase rounded-sm disabled:opacity-50"
                                            data-testid="auction-bid-submit"
                                        >
                                            {busy ? "Placing…" : "Place Bid"}
                                        </button>
                                    </form>
                                )}
                            </div>

                            <div className="mt-6 text-xs text-silver-500 flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5 text-platinum" />
                                Bids escrowed. Top bid at close enters a 24h acceptance window.
                            </div>
                        </div>

                        {/* RIGHT — bid ladder */}
                        <div className="glass rounded-sm p-6 max-h-[700px] overflow-y-auto" data-testid="auction-ladder">
                            <div className="text-[11px] tracking-[0.3em] uppercase text-silver-400 mb-5">Bid ladder ({bids.length})</div>
                            {bids.length === 0 ? (
                                <div className="text-silver-500 text-sm">No bids yet. Be the first.</div>
                            ) : (
                                <ol className="space-y-3">
                                    <AnimatePresence initial={false}>
                                        {bids.map((b, i) => (
                                            <motion.li
                                                key={b.id}
                                                initial={{ opacity: 0, x: 20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                className={`flex items-center justify-between px-4 py-3 border rounded-sm text-sm ${
                                                    i === 0 ? "border-platinum bg-platinum/5" : "border-white/5"
                                                }`}
                                            >
                                                <div>
                                                    <div className={i === 0 ? "text-white" : "text-silver-200"}>{b.bidder_name}</div>
                                                    <div className="text-[11px] text-silver-500">{new Date(b.created_at).toLocaleString()}</div>
                                                </div>
                                                <div className={`tabular-nums font-mono ${i === 0 ? "text-platinum text-base" : "text-silver-300"}`}>
                                                    ${Number(b.amount_usd).toLocaleString()}
                                                </div>
                                            </motion.li>
                                        ))}
                                    </AnimatePresence>
                                </ol>
                            )}
                        </div>
                    </div>
                )}

                <div className="mt-10">
                    <Link to={`/film/${filmId}`} className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-silver-400 hover:text-platinum">
                        View full film details <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
